import { createFileRoute, Link } from "@tanstack/react-router";
import { JsonLd } from "~/components/seo/JsonLd";
import {
  ATS_EVIDENCE_AI_TRANSPARENCY_HASH,
  ATS_EVIDENCE_CTA_LABEL,
  ATS_EVIDENCE_DESCRIPTION,
  ATS_EVIDENCE_FAQS,
  ATS_EVIDENCE_HEADING,
  ATS_EVIDENCE_LINKS,
  ATS_EVIDENCE_ROWS,
  ATS_EVIDENCE_SCHEMA_GRAPH,
  ATS_EVIDENCE_TITLE,
  ATS_EVIDENCE_URL,
  ATS_TRANSPARENT_AI_HEADING,
} from "~/lib/seo/ats-evidence";

export const Route = createFileRoute("/de/ats-lebenslauf-anforderungen-belegen")({
  head: () => ({
    meta: [
      { title: ATS_EVIDENCE_TITLE },
      { name: "description", content: ATS_EVIDENCE_DESCRIPTION },
      { property: "og:title", content: ATS_EVIDENCE_TITLE },
      { property: "og:description", content: ATS_EVIDENCE_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: ATS_EVIDENCE_URL },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow" },
    ],
    links: [{ rel: "canonical", href: ATS_EVIDENCE_URL }],
  }),
  component: AtsEvidenceLandingPage,
});

function AtsEvidenceLandingPage() {
  return (
    <div style={{ backgroundColor: "var(--bg)", color: "var(--text)" }}>
      <JsonLd json={ATS_EVIDENCE_SCHEMA_GRAPH} />

      <article className="mx-auto max-w-5xl px-5 pb-24 pt-16 sm:px-8 sm:pt-20">
        <header className="max-w-3xl">
          <p
            className="text-sm font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--accent-text)" }}
          >
            Anforderung → Nachweis → Formulierungslücke
          </p>
          <h1
            className="mt-4 text-4xl font-bold tracking-[-0.035em] sm:text-5xl"
            style={{ color: "var(--text)" }}
          >
            {ATS_EVIDENCE_HEADING}
          </h1>
          <p
            className="mt-6 text-lg leading-8 sm:text-xl"
            style={{ color: "var(--text-secondary)" }}
          >
            Ein nützlicher ATS-Check zeigt nicht nur passende Begriffe. Er ordnet jede
            Anforderung einer belegbaren Erfahrung zu und markiert, was unklar oder nicht
            belegt ist. Formulierungen dürfen verständlicher werden; Arbeitgeber, Daten,
            Abschlüsse, Werkzeuge und Ergebnisse dürfen nicht erfunden werden.
          </p>
          <p className="mt-4 text-base leading-7" style={{ color: "var(--text-secondary)" }}>
            Das Ergebnis zeigt, was belegt, unklar oder nicht vorhanden ist. Es sagt nicht
            voraus, wie ein bestimmtes System oder ein Arbeitgeber entscheidet.
          </p>
        </header>

        <section aria-labelledby="evidence-map-heading" className="mt-14">
          <div className="max-w-3xl">
            <h2 id="evidence-map-heading" className="text-2xl font-semibold tracking-tight">
              Anforderungen nachvollziehbar zuordnen
            </h2>
            <p className="mt-3 leading-7" style={{ color: "var(--text-secondary)" }}>
              Die folgenden Zeilen sind Beispiele für die Prüflogik. Sie beschreiben keine
              konkrete Stelle, keinen bestimmten Arbeitgeber und kein Bewerbungsergebnis.
            </p>
          </div>

          <div
            className="mt-7 overflow-x-auto rounded-2xl border"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--surface)" }}
          >
            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
              <caption className="sr-only">
                Beispielhafte Zuordnung von Stellenanforderungen zu vorhandenen Nachweisen,
                Formulierungslücken und einem textlichen Status
              </caption>
              <thead style={{ backgroundColor: "var(--bg-secondary)" }}>
                <tr>
                  {[
                    "Anforderung",
                    "Beleg im Lebenslauf",
                    "Formulierungslücke",
                    "Status",
                  ].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="border-b px-5 py-4 font-semibold"
                      style={{ borderColor: "var(--border)", color: "var(--text)" }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ATS_EVIDENCE_ROWS.map((row) => (
                  <tr key={row.requirement} className="align-top">
                    <th
                      scope="row"
                      className="border-b px-5 py-5 font-semibold"
                      style={{ borderColor: "var(--border)", color: "var(--text)" }}
                    >
                      {row.requirement}
                    </th>
                    <td
                      className="border-b px-5 py-5 leading-6"
                      style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                    >
                      {row.evidence}
                    </td>
                    <td
                      className="border-b px-5 py-5 leading-6"
                      style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                    >
                      {row.wordingGap}
                    </td>
                    <td
                      className="border-b px-5 py-5 font-semibold"
                      style={{ borderColor: "var(--border)", color: "var(--text)" }}
                    >
                      {row.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm leading-6" style={{ color: "var(--text-muted)" }}>
            <strong style={{ color: "var(--text)" }}>belegt</strong> = ein passender Nachweis
            ist vorhanden; <strong style={{ color: "var(--text)" }}>unklar</strong> = der
            Nachweis reicht für eine eindeutige Zuordnung nicht aus;{" "}
            <strong style={{ color: "var(--text)" }}>fehlt</strong> = im Lebenslauf ist kein
            entsprechender Nachweis angegeben.
          </p>
        </section>

        <div className="mt-16 grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
          <section aria-labelledby="review-heading">
            <h2 id="review-heading" className="text-2xl font-semibold tracking-tight">
              Vor dem Export: prüfen statt übernehmen
            </h2>
            <ol className="mt-6 space-y-6">
              {[
                {
                  title: "Anforderung lesen",
                  text: "Prüfe, was die Stellenanzeige tatsächlich verlangt und ob die Formulierung eindeutig ist.",
                },
                {
                  title: "Nachweis finden",
                  text: "Ordne nur Erfahrungen, Fähigkeiten oder Qualifikationen zu, die in deinen Angaben vorhanden sind.",
                },
                {
                  title: "Formulierung klären",
                  text: "Ergänze Kontext nur aus deinen Fakten. Unklare oder fehlende Belege bleiben gekennzeichnet.",
                },
                {
                  title: "Aussagen freigeben",
                  text: "Prüfe jede wesentliche Aussage selbst, bevor du den Lebenslauf exportierst oder verwendest.",
                },
              ].map((step, index) => (
                <li key={step.title} className="grid grid-cols-[2.5rem_1fr] gap-4">
                  <span
                    aria-hidden="true"
                    className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
                    style={{ backgroundColor: "var(--accent-subtle)", color: "var(--accent-text)" }}
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold">{step.title}</h3>
                    <p className="mt-1 leading-7" style={{ color: "var(--text-secondary)" }}>
                      {step.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <aside
            aria-labelledby="boundaries-heading"
            className="self-start rounded-2xl border p-6 sm:p-8"
            style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
          >
            <h2 id="boundaries-heading" className="text-xl font-semibold">
              Klare Grenzen für KI-Vorschläge
            </h2>
            <p className="mt-4 leading-7" style={{ color: "var(--text-secondary)" }}>
              Arbeitgeber, Zeiträume, Abschlüsse, Tools, Verantwortlichkeiten,
              Sprachkenntnisse, Zertifikate, Kennzahlen und Ergebnisse werden nicht erfunden.
            </p>
            <p className="mt-4 leading-7" style={{ color: "var(--text-secondary)" }}>
              Teile nur die Daten, die für deine Prüfung nötig sind. Hinweise zur aktuellen
              Verarbeitung findest du in der{" "}
              <Link to={ATS_EVIDENCE_LINKS.privacy} className="font-medium underline underline-offset-4">
                Datenschutzerklärung
              </Link>
              .
            </p>
            <p className="mt-4 leading-7" style={{ color: "var(--text-secondary)" }}>
              Ein Score fasst Zuordnungen zusammen. Er ist keine Zusage für technische
              Lesbarkeit, Auswahl, Einladung oder Einstellung.
            </p>
          </aside>
        </div>

        <section aria-labelledby="faq-heading" className="mt-20 max-w-3xl">
          <p
            className="text-sm font-semibold uppercase tracking-[0.16em]"
            style={{ color: "var(--accent-text)" }}
          >
            Transparente KI
          </p>
          <h2 id="faq-heading" className="mt-3 text-3xl font-semibold tracking-tight">
            {ATS_TRANSPARENT_AI_HEADING}
          </h2>
          <p className="mt-5 leading-7" style={{ color: "var(--text-secondary)" }}>
            KI kann vorhandene Angaben strukturieren, sprachlich verbessern und mit
            Anforderungen vergleichen. Sie sollte keine Arbeitgeber, Daten, Abschlüsse,
            Verantwortlichkeiten, Werkzeuge, Sprachkenntnisse, Zertifikate, Kennzahlen oder
            Ergebnisse ergänzen, die nicht belegt sind. Vor dem Export prüft die bewerbende
            Person jede wesentliche Aussage.
          </p>
          <dl className="mt-8 divide-y" style={{ borderColor: "var(--border)" }}>
            {ATS_EVIDENCE_FAQS.map((faq) => (
              <div key={faq.question} className="py-6 first:pt-0">
                <dt className="text-lg font-semibold">{faq.question}</dt>
                <dd className="mt-3 leading-7" style={{ color: "var(--text-secondary)" }}>
                  {faq.answer}
                </dd>
              </div>
            ))}
          </dl>
          <Link
            to={ATS_EVIDENCE_LINKS.aiTransparency}
            hash={ATS_EVIDENCE_AI_TRANSPARENCY_HASH}
            className="mt-2 inline-flex font-medium underline underline-offset-4"
          >
            Mehr über die bestehende KI-Transparenz bei Alchema
          </Link>
        </section>

        <section
          aria-labelledby="cta-heading"
          className="mt-20 rounded-3xl px-6 py-10 sm:px-10 sm:py-12"
          style={{ backgroundColor: "var(--accent-subtle)" }}
        >
          <div className="max-w-2xl">
            <h2 id="cta-heading" className="text-3xl font-semibold tracking-tight">
              Prüfe Anforderungen gegen deine eigenen Nachweise
            </h2>
            <p className="mt-4 leading-7" style={{ color: "var(--text-secondary)" }}>
              Starte mit deinem bestehenden Lebenslauf und entscheide selbst, welche
              Formulierungen zu deinem tatsächlichen Werdegang passen.
            </p>
            <Link
              to={ATS_EVIDENCE_LINKS.register}
              className="mt-7 inline-flex min-h-11 items-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
              style={{ backgroundColor: "var(--accent)" }}
            >
              {ATS_EVIDENCE_CTA_LABEL}
            </Link>
          </div>
        </section>

        <nav aria-label="Weiterführende Informationen" className="mt-12">
          <h2 className="text-lg font-semibold">Weiterführende Informationen</h2>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm">
            <li>
              <Link to={ATS_EVIDENCE_LINKS.pricing} className="underline underline-offset-4">
                Preise
              </Link>
            </li>
            <li>
              <Link to={ATS_EVIDENCE_LINKS.coverLetters} className="underline underline-offset-4">
                Anschreiben
              </Link>
            </li>
            <li>
              <Link to={ATS_EVIDENCE_LINKS.din5008} className="underline underline-offset-4">
                DIN-5008-Lebenslauf
              </Link>
            </li>
            <li>
              <Link to={ATS_EVIDENCE_LINKS.privacy} className="underline underline-offset-4">
                Datenschutz
              </Link>
            </li>
          </ul>
        </nav>
      </article>
    </div>
  );
}
